<script setup lang="ts">
import { computed, reactive } from 'vue'
import { NButton, NCheckbox, NInputNumber, NSelect, NTag, NTooltip } from 'naive-ui'
import { useAgentReposStore, type AgentRepoStatus } from '../stores/agent_repos'
import { useAsyncAction } from '../composables/useAsyncAction'
import {
  productOwnerCadenceStateBadgeClass,
  productOwnerCadenceStateExitClause,
  productOwnerCadenceStateSinceLabel,
} from '../composables/productOwnerCadenceState'
import { pollIntervalLabel } from '../composables/pollIntervalLabel'
import { useNow } from '../composables/useNow'
import StatusPill from '../components/StatusPill.vue'

// Opts a repo into the product-owner heartbeat and sets its ceilings. The
// check-in cadence itself is computed and read-only here. A DB value always
// wins over YAML, so there is no "inherit from YAML" state to represent.

// Agent::ProductOwnerCadence's clamp bounds; the API rejects anything outside them.
const MIN_INTERVAL_MINUTES = 15
const MAX_INTERVAL_MINUTES = 1440

// Preset choices for the max-interval dropdown (#1492) — a strict subset of
// the [MIN_INTERVAL_MINUTES, MAX_INTERVAL_MINUTES] range the backend still
// enforces. Round, human-meaningful bands rather than every step-15 value;
// 15 (one heartbeat tick) and 45 exist for pinning a repo under close watch.
const MAX_INTERVAL_OPTIONS = [15, 30, 45, 60, 120, 240, 360, 480, 720, 1440]

const props = defineProps<{ repo: AgentRepoStatus }>()
const store = useAgentReposStore()
const now = useNow()

// Names the state's own exit condition (#1625) alongside the always-present
// explanation below, mirroring ProductOwnerRunCard.vue's cadenceStateTitle.
const cadenceStateExitClause = computed(() =>
  productOwnerCadenceStateExitClause(props.repo.product_owner_cadence_state),
)

function centsToDollars(cents: number | null): number | null {
  return cents === null ? null : cents / 100
}

// Local draft, seeded once from the resolved value this repo card mounted
// with — not resynced from later poll ticks, mirroring the token draft's
// "edit locally, apply explicitly via Save" pattern in AdminAgentRepos.vue
// rather than AgentRepoDevcontainerActions' instant-apply checkbox (a
// bare checkbox can't also carry the ceiling amount atomically).
const draft = reactive({
  enabled: props.repo.product_owner_enabled,
  costCeilingDollars: centsToDollars(props.repo.product_owner_cost_ceiling_cents),
  runCostCeilingDollars: centsToDollars(props.repo.product_owner_run_cost_ceiling_cents),
  maxIntervalMinutes: props.repo.product_owner_max_interval_minutes,
})

// The preset list plus, when it's already stored, the current value itself —
// a repo saved under the old free-numeric input can hold an off-preset value
// (e.g. 90), and dropping it here would show the dropdown as blank despite
// draft.maxIntervalMinutes still holding a valid number.
const maxIntervalOptions = computed(() => {
  const minutes = draft.maxIntervalMinutes
  const values =
    minutes && !MAX_INTERVAL_OPTIONS.includes(minutes)
      ? [...MAX_INTERVAL_OPTIONS, minutes].toSorted((a, b) => a - b)
      : MAX_INTERVAL_OPTIONS
  return values.map((value) => ({ label: pollIntervalLabel(value), value }))
})

// Mirrors AgentRepo's validations so Save doesn't round-trip a guaranteed 422.
const ceilingInvalid = computed(() => draft.enabled && !(draft.costCeilingDollars && draft.costCeilingDollars > 0))
const maxIntervalInvalid = computed(
  () =>
    draft.enabled &&
    !(
      draft.maxIntervalMinutes &&
      draft.maxIntervalMinutes >= MIN_INTERVAL_MINUTES &&
      draft.maxIntervalMinutes <= MAX_INTERVAL_MINUTES
    ),
)
const saveDisabled = computed(() => ceilingInvalid.value || maxIntervalInvalid.value)

// A silently-greyed Save is the same trap as the once-unlabeled ceiling
// fields — name the unmet condition where the admin is already looking.
const saveDisabledReason = computed(() => {
  if (ceilingInvalid.value) return 'Enter an hourly ceiling above $0 to enable.'
  if (maxIntervalInvalid.value)
    return `Enter a max interval between ${MIN_INTERVAL_MINUTES} and ${MAX_INTERVAL_MINUTES} minutes.`
  return null
})

const { loading: saving, run: save } = useAsyncAction(
  () =>
    store.update(props.repo.slug, {
      product_owner_enabled: draft.enabled,
      // Only relevant (and only sent) when enabling — disabling never needs
      // a ceiling/max interval, and blank/unset means "leave the stored value
      // untouched" (matches the token field's not-overwritten-when-blank
      // semantics).
      ...(draft.enabled && draft.costCeilingDollars
        ? { product_owner_cost_ceiling_cents: Math.round(draft.costCeilingDollars * 100) }
        : {}),
      ...(draft.enabled && draft.runCostCeilingDollars
        ? { product_owner_run_cost_ceiling_cents: Math.round(draft.runCostCeilingDollars * 100) }
        : {}),
      ...(draft.enabled && draft.maxIntervalMinutes
        ? { product_owner_max_interval_minutes: draft.maxIntervalMinutes }
        : {}),
    }),
  {
    successMessage: () => `${props.repo.slug} product-owner setting saved`,
    failureLabel: 'Failed to update product-owner setting',
  },
)
</script>

<template>
  <div class="flex items-center gap-2 flex-wrap">
    <NTooltip placement="top">
      <template #trigger>
        <NCheckbox v-model:checked="draft.enabled" :data-testid="`agent-repo-product-owner-enabled-${repo.slug}`">
          Product owner
        </NCheckbox>
      </template>
      Opts this repo into the recurring product-owner heartbeat: an automated agent session that checks in on the repo
      and spends real money each run, bounded by the two ceilings.
    </NTooltip>
    <NTooltip v-if="draft.enabled" placement="top">
      <template #trigger>
        <NInputNumber
          v-model:value="draft.costCeilingDollars"
          size="small"
          :min="0.01"
          :precision="2"
          placeholder="Hourly ceiling ($)"
          class="w-36"
          :data-testid="`agent-repo-product-owner-ceiling-${repo.slug}`"
        />
      </template>
      Calendar-hour budget: a new heartbeat run is refused when this repo's product-owner spend since the top of the
      current hour has already reached this amount.
    </NTooltip>
    <NTooltip v-if="draft.enabled" placement="top">
      <template #trigger>
        <NInputNumber
          v-model:value="draft.runCostCeilingDollars"
          size="small"
          :min="0.01"
          :precision="2"
          placeholder="Per-run ceiling ($)"
          class="w-36"
          :data-testid="`agent-repo-product-owner-run-ceiling-${repo.slug}`"
        />
      </template>
      Single-run budget: a run in progress is cancelled once its own cost reaches this amount — catches one runaway run
      the hourly gate can't.
    </NTooltip>
    <NTooltip v-if="draft.enabled" placement="top">
      <template #trigger>
        <NTag size="small" :data-testid="`agent-repo-product-owner-poll-interval-${repo.slug}`">
          Checks every {{ pollIntervalLabel(repo.product_owner_poll_interval_minutes) }}
        </NTag>
      </template>
      Computed automatically from this repo's own open-issue trend (#1407) — grows when the backlog is flat or
      shrinking, shrinks when it's growing. No longer a manual setting; see the repo's card on /admin/product-owner-runs
      for its actual next check time.
    </NTooltip>
    <NTooltip v-if="draft.enabled" placement="top">
      <template #trigger>
        <StatusPill
          :label="
            productOwnerCadenceStateSinceLabel(
              repo.product_owner_cadence_state,
              repo.product_owner_cadence_state_entered_at,
              now,
            )
          "
          :color-class="productOwnerCadenceStateBadgeClass(repo.product_owner_cadence_state)"
          :data-testid="`agent-repo-product-owner-cadence-state-${repo.slug}`"
        />
      </template>
      The cadence algorithm's own read-only label (#1536) for what its inputs currently look like — growing/flat/
      shrinking from the daily trend, bursting from a live spike, or idle after several no-progress runs.
      {{ cadenceStateExitClause }}
    </NTooltip>
    <NTooltip v-if="draft.enabled" placement="top">
      <template #trigger>
        <NSelect
          v-model:value="draft.maxIntervalMinutes"
          size="small"
          :options="maxIntervalOptions"
          placeholder="Max interval"
          class="w-36"
          :data-testid="`agent-repo-product-owner-max-interval-${repo.slug}`"
        />
      </template>
      How slow this repo's check-ins may get at most, in minutes. The cadence algorithm relaxes the interval on a quiet
      backlog but never past this ceiling — default 1440 (once a day).
    </NTooltip>
    <NTooltip placement="top" :disabled="!saveDisabledReason">
      <!-- Span wrapper: a disabled native button doesn't emit the hover
           events the tooltip listens for in all browsers. -->
      <template #trigger>
        <span class="inline-flex" :tabindex="saveDisabledReason ? 0 : undefined">
          <NButton
            size="small"
            type="primary"
            :loading="saving"
            :disabled="saveDisabled"
            :data-testid="`agent-repo-product-owner-save-${repo.slug}`"
            @click="save"
            >Save</NButton
          >
        </span>
      </template>
      {{ saveDisabledReason }}
    </NTooltip>
  </div>
</template>
